import { useModal } from "@/Contexts";
import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import {
  getContaminantName,
  getWasteTypeName,
} from "@/Utils/commonExportFunctions";
import { trpc } from "@/Utils/trpc";
import type { ContaminationDetails } from "@/pages/events/types";
import { useTrans } from "@/translations";

import { useCallback, useMemo } from "react";

import type { ListEventsContaminantsImageDto } from "server/api/index.js";

type Props = {
  eventId: string;
  contaminationDetails: ContaminationDetails;
};

export const ContaminationDetailsView: React.FC<Props> = ({
  eventId,
  contaminationDetails,
}: Props) => {
  const { t } = useTrans();
  const { showModal, hideModal } = useModal();
  const { wasteTypes: sanityWasteTypes, otherContaminants } =
    useCommonEntitiesStore();

  const { data: eventContaminants, isLoading: isLoadingContaminants } =
    trpc.events.getEventContaminants.useQuery({
      id: eventId,
    });

  const {
    data: eventContaminationImages,
    isLoading: isLoadingContaminationImages,
  } = trpc.events.getEventImages.useQuery(
    {
      id: eventId,
    },
    {
      enabled: contaminationDetails.hasImages,
    },
  );

  const expandImage = useCallback(
    (e: React.MouseEvent, image: ListEventsContaminantsImageDto) => {
      e.stopPropagation();
      e.preventDefault();

      showModal(
        <div
          className="relative flex h-full w-full items-center justify-center"
          onMouseDown={(e) => e.currentTarget === e.target && hideModal()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <img
            src={`data:${image.mediaType};${image.encoding},${image.data}`}
            className="max-h-[90%] object-contain"
          />
        </div>,
      );
    },
    [showModal, hideModal],
  );

  const contaminantsData = useMemo(() => {
    if (!eventContaminants?.items?.length) {
      return [];
    }

    const itemsWithData = eventContaminants.items.filter(
      (item) => item.contaminantType || item.wasteType,
    );

    if (!itemsWithData.length) {
      return [];
    }

    const mappedItemsWithData = eventContaminants.items.map((item, i) => ({
      id: `contaminant_${i}`,
      name: item.wasteType
        ? getWasteTypeName(sanityWasteTypes!, item.wasteType?.code || "")
        : getContaminantName(
            otherContaminants!,
            item.contaminantType?.code || "",
          ),
    }));

    return mappedItemsWithData;
  }, [eventContaminants, otherContaminants, sanityWasteTypes]);

  if (
    isLoadingContaminants ||
    (contaminationDetails.hasImages && isLoadingContaminationImages)
  ) {
    return (
      <div className="border-gray2 absolute top-[54px] z-2 flex w-full items-center justify-center border border-1 bg-white p-4">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="border-gray2 absolute top-[54px] left-[-50px] z-2 w-[250px] border border-1 bg-white p-4">
      {contaminationDetails.wasteTypeIntended && (
        <div className="mb-4">
          <div className="font-bold">
            {t("genericLabels:intendedWasteStream")}
          </div>
          <div>
            {getWasteTypeName(
              sanityWasteTypes!,
              contaminationDetails.wasteTypeIntended.code || "",
            ) || contaminationDetails.wasteTypeIntended.code}
          </div>
        </div>
      )}
      <div>
        <div className="font-bold">{t("genericLabels:contaminants")}</div>
        {contaminantsData.length
          ? contaminantsData.map((c) => <div key={c.id}>{c.name}</div>)
          : "-"}
      </div>

      <div className="overflow-hidden">
        <div className="mt-4 font-bold">{t("genericLabels:notes")}</div>
        <div>{contaminationDetails.note || "-"}</div>
      </div>

      {contaminationDetails.hasImages && (
        <div>
          <div className="mt-4 font-bold">{t("genericLabels:images")}</div>
          <div className="flex">
            {eventContaminationImages?.items.map((image, index) => (
              <div
                key={`${image.mediaType}-${index}`}
                className="bg-grey1 mt-2 mr-2 h-[20px] w-[20px] cursor-pointer"
                onClick={(e) => expandImage(e, image)}
                style={{
                  backgroundImage: `url(data:${image.mediaType};${image.encoding},${image.data})`,
                  backgroundSize: "cover",
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
